import React, { useEffect, useMemo } from 'react';
import { Select, SelectItem, Selection } from '@nextui-org/react';
import { Settlement } from 'common/src/utils/types';
import { useTranslation } from '../../hooks/useTranslation';

interface CountryChartFilterProps {
  data: Settlement[];
  selectedCountry: string | string[];
  onSelectionChange: (value: string[]) => void;
  selectionMode?: 'single' | 'multiple';
  label?: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  defaultSelectAll?: boolean;
}

const CountryChartFilter: React.FC<CountryChartFilterProps> = ({
  data,
  selectedCountry,
  onSelectionChange,
  selectionMode = 'single',
  label,
  className = 'min-w-32',
  size = 'sm',
  defaultSelectAll = false,
}) => {
  const { t } = useTranslation('common');
  const finalLabel = label || t('País');

  const countries = useMemo(() => {
    const countryCount = data.reduce((acc, item) => {
      const country = item.market?.country || 'Sin País';
      acc[country] = (acc[country] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return Object.entries(countryCount)
      .sort(([, countA], [, countB]) => countB - countA)
      .map(([country]) => country);
  }, [data]);

  useEffect(() => {
    if (selectionMode === 'multiple' && defaultSelectAll && countries.length > 0) {
      onSelectionChange(countries);
    }
  }, []);

  if (countries.length === 0) return null;

  const handleSelectionChange = (keys: Selection) => {
    if (selectionMode === 'multiple') {
      const keysArray = Array.from(keys);
      if (keysArray.includes('all')) {
        if (Array.isArray(selectedCountry) && selectedCountry.length > 0) {
          onSelectionChange([]);
        } else {
          onSelectionChange(countries);
        }
      } else {
        onSelectionChange(keysArray as string[]);
      }
    } else {
      onSelectionChange(Array.from(keys) as string[]);
    }
  };

  return (
    <Select
      label={finalLabel}
      selectedKeys={
        selectionMode === 'multiple'
          ? Array.isArray(selectedCountry)
            ? new Set(selectedCountry)
            : new Set([])
          : new Set([selectedCountry as string])
      }
      onSelectionChange={handleSelectionChange}
      selectionMode={selectionMode}
      className={className}
      size={size}
      aria-label={t('Selecciona país')}
      renderValue={(items) => {
        if (
          Array.isArray(selectedCountry) &&
          selectedCountry.length === countries.length &&
          selectedCountry.length > 0
        ) {
          return <div className='flex items-center gap-2'>{t('Todos los países')}</div>;
        }
        if (Array.isArray(selectedCountry) && selectedCountry.length > 0) {
          const displayCount = 2;
          const first = selectedCountry.slice(0, displayCount);
          const remaining = selectedCountry.length - displayCount;
          return (
            <div className='flex items-center gap-2'>
              {first.join(', ')}
              {remaining > 0 && ` +${remaining}`}
            </div>
          );
        }
        return items;
      }}
    >
      {selectionMode === 'multiple' && (
        <SelectItem key='all' value='all' textValue={t('Todos')}>
          <div className='flex justify-between items-center'>
            <span>
              {Array.isArray(selectedCountry) && selectedCountry.length > 0
                ? t('Deseleccionar todos')
                : t('Seleccionar todos')}
            </span>
          </div>
        </SelectItem>
      )}
      {countries.map((country) => (
        <SelectItem key={country} value={country} textValue={country}>
          {country}
        </SelectItem>
      ))}
    </Select>
  );
};

export default React.memo(CountryChartFilter);
